/**
 * 魔方记法解析（Singmaster notation）—— 自 notation.js 平移，FACE_TABLE 语义已验证，禁止改动
 */
import type { Face } from './cube';

export interface Move {
  face: Face;
  axis: 'x' | 'y' | 'z';
  layer: 1 | -1;
  dir: 1 | -1;
  turns: 1 | 2;
}

/** 总旋转角度（弧度） */
export function moveAngle(m: Move): number {
  return (m.dir * m.turns * Math.PI) / 2;
}

/**
 * 每一面：旋转轴、所选层（cubie 逻辑坐标值）、基础方向。
 * 方向符号约定（从该面外侧朝中心看，顺时针为 +1）——与 3D 坐标系的对应关系已在旧版验证。
 */
const FACE_TABLE: Record<Face, { axis: 'x' | 'y' | 'z'; layer: 1 | -1; dir: 1 | -1 }> = {
  U: { axis: 'y', layer: 1, dir: -1 },
  D: { axis: 'y', layer: -1, dir: 1 },
  R: { axis: 'x', layer: 1, dir: -1 },
  L: { axis: 'x', layer: -1, dir: 1 },
  F: { axis: 'z', layer: 1, dir: -1 },
  B: { axis: 'z', layer: -1, dir: 1 },
};

/** 解析单步记法 → Move（非法返回 null） */
export function parseMove(token: string): Move | null {
  const t = token.trim();
  if (!t) return null;
  const face = t[0].toUpperCase() as Face;
  const info = FACE_TABLE[face];
  if (!info) return null;
  let dir = info.dir;
  let turns: 1 | 2 = 1;
  for (let i = 1; i < t.length; i++) {
    if (t[i] === "'") dir = (dir * -1) as 1 | -1;
    else if (t[i] === '2') turns = 2;
    else return null;
  }
  return { face, axis: info.axis, layer: info.layer, dir, turns };
}

/** 解析整串记法 → Move[] */
export function parseMoves(str: string): Move[] {
  return str
    .trim()
    .split(/\s+/)
    .map(parseMove)
    .filter((m): m is Move => m !== null);
}

/** 该层选择判据：给定 cubie 逻辑坐标是否属于某 Move 的层 */
export function isInLayer(move: Move, [x, y, z]: [number, number, number]): boolean {
  switch (move.axis) {
    case 'x':
      return (move.layer > 0 ? x : -x) > 0;
    case 'y':
      return (move.layer > 0 ? y : -y) > 0;
    case 'z':
      return (move.layer > 0 ? z : -z) > 0;
  }
}

/** 求某记法的逆：R → R'，R' → R，R2 → R2 */
export function inverseToken(token: string): string {
  if (token.endsWith("'")) return token.slice(0, -1);
  if (token.endsWith('2')) return token;
  return token + "'";
}

/** 全部 18 种基本转动记法（自由练习用） */
export const ALL_MOVES: string[] = (['U', 'D', 'L', 'R', 'F', 'B'] as const).flatMap((f) => [
  f,
  `${f}'`,
  `${f}2`,
]);

/** 生成随机打乱序列（不连转同面） */
export function randomScramble(length = 20): string[] {
  const out: string[] = [];
  let lastFace = '';
  while (out.length < length) {
    const t = ALL_MOVES[Math.floor(Math.random() * ALL_MOVES.length)];
    if (t[0] === lastFace) continue;
    lastFace = t[0];
    out.push(t);
  }
  return out;
}
